import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts } from '../../components/LessonUI';

const contentManipulationCode = `// 1. textContent - The security-first choice
// Fast, bypasses the HTML parser, and prevents XSS
const message = document.querySelector('.status');
message.textContent = "Data successfully saved.";

// 2. innerHTML - Use ONLY for trusted templates
const wrapper = document.querySelector('.card-wrapper');
wrapper.innerHTML = \`<div class="card">
    <h3>Title</h3>
    <p>Dynamic Content</p>
</div>\`;

// 3. outerHTML - Replacing the element itself
const oldBtn = document.querySelector('#delete-btn');
oldBtn.outerHTML = '<span class="status-disabled">Deleted</span>';`;

const classStyleManagerCode = `// 1. classList - The Modern State Manager
const element = document.querySelector('.accordion-item');

element.classList.add('is-open');
element.classList.toggle('highlighted');
element.classList.replace('theme-light', 'theme-dark');

// 2. inline styles - Use for dynamic values (e.g., coordinates)
const tooltip = document.querySelector('.tooltip');
tooltip.style.left = \`\${mouseX}px\`;
tooltip.style.top = \`\${mouseY}px\`;

// 3. computed styles - Reading current rendered values
const styles = window.getComputedStyle(element);
console.log("Current font size:", styles.fontSize);`;

const attributeManagementCode = `// 1. Dataset - Decoupling logic from presentation
const userCard = document.querySelector('.user-card');
userCard.dataset.userId = "42"; // Creates data-user-id="42"
userCard.dataset.status = "active";

// 2. Standard Attributes
const link = document.querySelector('a.profile-link');
link.setAttribute('href', \`/profile/\${userCard.dataset.userId}\`);
link.setAttribute('aria-expanded', 'true'); // For Accessibility

// 3. Boolean Attributes
const submitBtn = document.querySelector('button[type="submit"]');
submitBtn.disabled = true;
submitBtn.required = false;`;

const manipulationPerformanceCode = `// Minimizing Reflow Cycles

const list = document.querySelector('.user-list');

//  ANTI-PATTERN: Direct loops causing layout thrashing
data.forEach(item => {
    const li = document.createElement('li');
    li.textContent = item.name;
    list.appendChild(li); // Triggers layout calculation every time!
});

// BEST PRACTICE: Using DocumentFragment
const fragment = document.createDocumentFragment();
data.forEach(item => {
    const li = document.createElement('li');
    li.textContent = item.name;
    fragment.appendChild(li); // Fragment is in-memory only
});
list.appendChild(fragment); // Single DOM injection, single reflow

// ALTERNATIVE: Building HTML Strings (Fast for bulk simple lists)
const htmlStrings = data.map(item => \`<li>\${item.name}</li>\`).join('');
list.innerHTML = htmlStrings; // Single parser invocation`;

const JSDOMManipulation = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">DOM Manipulation</div>
                <h1>Mutating the Document Tree</h1>
                <p className="hero-lead">
                    Master the mechanics of DOM mutation. Learn to manage element state
                    through classes, manipulate content safely to prevent XSS, and
                    implement performance-driven insertion patterns that minimize
                    expensive browser reflow cycles.
                </p>
            </div>

            <div className="content-section">
                <h2>Content Mutation: Safety vs. Flexibility</h2>
                <p>
                    Changing the text or HTML content of an element is the most frequent mutation
                    performed in web applications. However, the choice between `textContent`
                    and `innerHTML` is an architectural decision with significant security
                    implications. `textContent` is the preferred choice for regular data
                    display because it treats the input as raw text, bypassing the browser's
                    HTML parser and neutralizing any potential script injection attacks. It
                    is also significantly faster because it doesn't trigger the complex parsing
                    and DOM-tree-building logic required by HTML strings. Only use `innerHTML`
                    when you are absolutely certain of the data's origin or after
                    performing rigorous sanitization, as it remains the primary vector
                    for Cross-Site Scripting (XSS) vulnerabilities in modern JS apps.
                </p>
                <p>
                    Beyond just text, we occasionally use `outerHTML` to replace an entire
                    element node with a new structure. This is useful for "destroy-and-rebuild"
                    scenarios, but it comes with a major caveat: replacing an element invalidates
                    any existing JavaScript references to the original node. If you have
                    listeners attached to the old variable, they will no longer function
                    relative to the new elements on the page. For most dynamic UIs, it is
                    architecturally superior to modify the internal state of an element
                    via `classList` or `dataset` rather than replacing the node itself.
                    This preserves identity, keeps event listeners intact, and results
                    in smoother visual transitions for the end-user.
                </p>

                <CodeBlock code={contentManipulationCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Classes as State Management</h2>
                <p>
                    Modern front-end development treats CSS classes as the "state machine" of the
                    View layer. Instead of manually changing individual inline styles (like
                    `element.style.color = 'red'`), we should define these states in CSS
                    (e.g., `.is-error`) and toggle them via the `classList` API. This
                    separation of concerns ensures that your JavaScript handles the
                    "logic" (when should it be red?) while your CSS handles the "presentation"
                    (what shade of red?). The `classList` API provides highly intuitive
                    methods like `toggle` and `replace`, which allow you to manage complex
                    UI states like "active," "collapsed," or "loading" with just a single
                    line of code.
                </p>
                <p>
                    Inline styles (`element.style`) should be reserved for truly dynamic,
                    runtime-calculated values that cannot be predefined in a stylesheetsuch
                    as the position of a custom tooltip following a mouse cursor or the
                    percentage width of a progress bar. For reading the current state
                    of an element, always use `window.getComputedStyle()`. Unlike
                    the `.style` property which only shows inline values, computed
                    styles give you the final, resolved values after CSS inheritance
                    and specificity have been applied by the engine. Understanding
                    the lifecycle of these style updates is key to implementing
                    performant animations and responsive layouts that don't
                    clutter the DOM with redundant style attributes.
                </p>

                <CodeBlock code={classStyleManagerCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Attributes and the Dataset Interface</h2>
                <p>
                    Attributes define the static and dynamic properties of your HTML elements.
                    While standard attributes like `src` or `id` have direct mappings
                    to JavaScript properties, custom data-attributes (`data-*`) are
                    best managed through the `dataset` API. The dataset interface
                    automatically converts kebab-case HTML attributes (like `data-user-id`)
                    into camelCase JavaScript properties (`userId`), providing a clean,
                    object-oriented way to store metadata directly on the DOM nodes.
                    This is particularly powerful for building components that need to
                    retrieve IDs or configuration values during event handling without
                    relying on global variables.
                </p>
                <p>
                    When working with high-access attributes, the `setAttribute` and
                    `getAttribute` methods provide a language-independent way to
                    manipulate node properties. For boolean attributes like `disabled`,
                    `checked`, or `required`, modern JavaScript allows you to simply
                    assign a boolean value directly to the property, which is more
                    intuitive than managing the string "true" or "false". Additionally,
                    remember that attributes are the primary way to communicate with
                    Assistive Technologies (like screen readers) via the ARIA standard.
                    Managing attributes like `aria-expanded` or `role` is not just
                    codingit is essential accessibility engineering that ensures
                    your dynamic UI is usable by everyone.
                </p>

                <CodeBlock code={attributeManagementCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Reflow Management and Performance</h2>
                <p>
                    The most expensive part of DOM manipulation isn't the JavaScript code,
                    but the subsequent "Reflow" or "Layout" phase performed by the browser.
                    Each time you insert a node into the live DOM, the browser may
                    recalculate the geometry of every element on the page. To avoid this
                    performance tax, senior engineers use **Batch Insertion** patterns.
                    The `DocumentFragment` is the industry-standard tool for this:
                    it acts as a lightweight, invisible document where you can construct
                    massive subtrees in memory. Once the fragment is complete, a single
                    injection into the live DOM triggers exactly one reflow, regardless
                    of how many elements were inside the fragment.
                </p>
                <p>
                    Another strategy for bulk updates is building your HTML as a large
                    string and using a single `innerHTML` assignment. While this is
                    often faster for simple structures because it uses the browser's
                    highly optimized C++ parser, it has a significant drawback: it
                    wipes out any existing event listeners inside the target container.
                    For reactive UIs, the "Fragment" approach or modern insertion
                    methods like `append()` and `prepend()` are usually preferred as
                    they preserve element identity and memory bindings. By
                    understanding the hidden costs of layout calculations, you
                    transform your code from simple scripting into high-performance
                    UI orchestration that remains fluid even on mobile hardware.
                </p>

                <CodeBlock code={manipulationPerformanceCode} language="javascript" />
            </div>

            <KeyFacts><ul>
                    <li>**Security:** Prioritize `textContent` for raw data to prevent XSS.</li>
                    <li>**Logic:** Use `classList` to manage UI states via predefined CSS.</li>
                    <li>**Context:** Read current layout values using `getComputedStyle()`.</li>
                    <li>**Decoupling:** Use `dataset` to store IDs and configuration metadata.</li>
                    <li>**Accessibility:** Bridge JS logic to screen readers via ARIA attributes.</li>
                    <li>**Batching:** Use `DocumentFragment` for bulk insertions to minimize reflows.</li>
                    <li>**Consistency:** Prefer properties (el.id) over attributes (setAttribute) for common keys.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSDOMManipulation;

